import React, { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, FileText, Calendar, MapPin, Tag, Eye, Trash2 } from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { Panel, AdminInput, AdminTextarea, AdminSelect } from "@/admin/components/ui";
import { EventPricingPanel } from "@/admin/components/EventPricingPanel";
import CoverImageUploader from "@/admin/components/CoverImageUploader";

const CATEGORIES = [
  "Sports", "Corporate", "Wedding", "Engagement", "Baby Shower",
  "Matric Farewell", "Outdoor Activity", "Individual Photoshoot",
  "Promotional Event", "Business Photography", "Lodge Photography", "Other"
];

const empty = {
  title: "", date: "", category: "Sports", location: "",
  short_description: "", full_description: "", cover_image: "",
  cover_image_manual: "",
  status: "draft", visibility: "public", slug: "", notes: "",
};

export default function AdminEventForm({ mode = "create" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(mode === "edit");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (mode === "edit" && id) {
      adminApi.get(`/admin/events/${id}`).then((r) => {
        const ev = r.data || {};
        const ci = ev.cover_image || "";
        // The uploader-managed cover is a relative /api/public/event-covers/{id}?v=...
        // URL. Anything else that the admin had previously typed in by hand is shown
        // in the advanced manual field instead.
        const isUploaded = typeof ci === "string" && ci.startsWith("/api/public/event-covers/");
        setForm({
          ...empty,
          ...ev,
          cover_image: ci,
          cover_image_manual: isUploaded ? "" : (ci || ""),
        });
      }).finally(() => setLoading(false));
    }
  }, [mode, id]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Receives partial updates from CoverImageUploader (either an uploaded URL,
  // a manual-URL change, or both being cleared).
  const onCoverChange = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const save = async (overrideStatus) => {
    if (!form.title || !form.date || !form.category) {
      toast.error("Title, date and category are required.");
      return;
    }
    setBusy(true);
    // Final cover_image precedence:
    //   1. Uploaded proxy URL (form.cover_image starts with /api/public/event-covers/…)
    //   2. Manual URL fallback (form.cover_image_manual)
    //   3. Empty
    let finalCover = "";
    if (typeof form.cover_image === "string" && form.cover_image.startsWith("/api/public/event-covers/")) {
      finalCover = form.cover_image;
    } else if (form.cover_image_manual && form.cover_image_manual.trim()) {
      finalCover = form.cover_image_manual.trim();
    }
    const { cover_image_manual: _drop, ...rest } = form;
    const payload = { ...rest, cover_image: finalCover };
    if (overrideStatus) payload.status = overrideStatus;
    try {
      if (mode === "edit") {
        await adminApi.put(`/admin/events/${id}`, payload);
        toast.success("Event updated.");
      } else {
        await adminApi.post("/admin/events", payload);
        toast.success("Event created.");
        navigate("/admin/events", { replace: true });
        return;
      }
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this event? This also removes its subfolders.")) return;
    try {
      await adminApi.delete(`/admin/events/${id}`);
      toast.success("Event deleted.");
      navigate("/admin/events", { replace: true });
    } catch (err) { toast.error(formatErr(err)); }
  };

  if (loading) return <p className="text-sm text-white/40">Loading…</p>;

  return (
    <div data-testid="page-admin-event-form" className="space-y-6 max-w-5xl">
      <SEO title="AdminEventForm · WelkomBiz Photography" noindex />
      <Link to="/admin/events" className="inline-flex items-center gap-2 text-[10px] tracking-luxe uppercase text-white/55 hover:text-white">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Events
      </Link>

      <Panel title="Cover Image" testId="event-form-cover">
        <CoverImageUploader
          eventId={mode === "edit" ? id : null}
          value={form.cover_image}
          manualValue={form.cover_image_manual}
          onChange={onCoverChange}
          disabled={busy}
        />
        {mode !== "edit" && !form.cover_image && !form.cover_image_manual && (
          <p data-testid="cover-save-first-hint" className="text-[11px] text-white/55 mt-3 font-body">
            Tip · Save the event as a draft first, then upload a cover image — uploads
            are saved against an existing event so they can be replaced safely later.
          </p>
        )}
      </Panel>

      <Panel testId="event-form-main">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <AdminInput data-testid="event-input-title" label="Event Name" icon={FileText} value={form.title} onChange={update("title")} placeholder="Goldfields Sports Event" />
          <AdminInput data-testid="event-input-date"  label="Event Date" icon={Calendar} type="date" value={form.date} onChange={update("date")} />
          <AdminSelect data-testid="event-input-category" label="Category" value={form.category} onChange={update("category")}>
            {CATEGORIES.map((c) => <option key={c} value={c} className="bg-black">{c}</option>)}
          </AdminSelect>
          <AdminInput data-testid="event-input-location" label="Location" icon={MapPin} value={form.location} onChange={update("location")} placeholder="Goldfields Stadium, Welkom" />
          <AdminTextarea data-testid="event-input-short" label="Short Description" value={form.short_description} onChange={update("short_description")} rows={2} className="md:col-span-2" placeholder="A 1–2 line summary that appears on the gallery card." />
          <AdminTextarea data-testid="event-input-full" label="Full Description" value={form.full_description} onChange={update("full_description")} rows={5} className="md:col-span-2" placeholder="The full story of the event, the coverage notes, the mood…" />
        </div>
      </Panel>

      <Panel title="Publishing" testId="event-form-publishing">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <AdminSelect data-testid="event-input-status" label="Status" icon={Tag} value={form.status} onChange={update("status")}>
            <option value="draft" className="bg-black">Draft</option>
            <option value="published" className="bg-black">Published</option>
            <option value="archived" className="bg-black">Archived</option>
          </AdminSelect>
          <AdminSelect data-testid="event-input-visibility" label="Visibility" icon={Eye} value={form.visibility} onChange={update("visibility")}>
            <option value="public" className="bg-black">Public</option>
            <option value="private" className="bg-black">Private</option>
          </AdminSelect>
          <AdminInput data-testid="event-input-slug" label="Slug (URL)" value={form.slug} onChange={update("slug")} placeholder="auto-generated from title" />
        </div>
        <AdminTextarea data-testid="event-input-notes" label="Admin Notes" value={form.notes} onChange={update("notes")} rows={3} className="mt-5" placeholder="Internal notes for the team — not shown to the public." />
      </Panel>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          <button data-testid="event-save-draft" disabled={busy} onClick={() => save("draft")} className="btn-ghost !py-2.5 !px-5 !text-[11px] disabled:opacity-60">
            <Save className="w-3.5 h-3.5" /> Save Draft
          </button>
          <button data-testid="event-publish" disabled={busy} onClick={() => save("published")} className="btn-primary !py-2.5 !px-5 !text-[11px] disabled:opacity-60">
            <Save className="w-3.5 h-3.5" /> Publish Event
          </button>
          <Link to="/admin/events" className="btn-ghost !py-2.5 !px-5 !text-[11px]">Cancel</Link>
        </div>
        {mode === "edit" && (
          <button data-testid="event-delete" onClick={remove} className="text-[10px] tracking-luxe uppercase text-white/40 hover:text-[#ff7fc1] inline-flex items-center gap-2">
            <Trash2 className="w-3.5 h-3.5" /> Delete Event
          </button>
        )}
      </div>
    </div>
  );
}
